// ======================================================================
// 📁 File: src/modules/ppe/components/engineering/product-variant/ProductVariantDrawer.jsx
// 📝 Purpose:
//   Drawer-based Create / Edit Product Variant
//   - ERP-grade validation
//   - Matches ppe_product_variant table EXACTLY
//   - Audit-safe
//   - No routing break
// ======================================================================

import React, { useEffect, useState } from "react";
import axiosClient from "../../../../../api/axiosClient";

export default function ProductVariantDrawer({
  open,
  mode = "create", // create | edit
  record = null,
  productFamilies = [], // dropdown data
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState({
    product_family_id: "",
    variant_code: "",
    name: "",
    description: "",
    drawing_required: true,
    routing_required: true,
    is_active: 1,
    status_id: 1,
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ------------------------------------------------------------
  // Initialize form
  // ------------------------------------------------------------
  useEffect(() => {
    if (mode === "edit" && record) {
      setForm({
        product_family_id: record.product_family_id,
        variant_code: record.variant_code || "",
        name: record.name || "",
        description: record.description || "",
        drawing_required: !!record.drawing_required,
        routing_required: !!record.routing_required,
        is_active: record.is_active ? 1 : 0,
        status_id: record.status_id || 1,
      });
    }

    if (mode === "create") {
      setForm({
        product_family_id: "",
        variant_code: "",
        name: "",
        description: "",
        drawing_required: true,
        routing_required: true,
        is_active: 1,
        status_id: 1,
      });
    }
  }, [mode, record]);

  if (!open) return null;

  const onChange = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
  };

  // ------------------------------------------------------------
  // Save
  // ------------------------------------------------------------
  const onSubmit = async () => {
    setError(null);

    if (!form.product_family_id || !form.variant_code || !form.name) {
      setError("Product Family, Variant Code and Name are mandatory");
      return;
    }

    setSaving(true);
    try {
      if (mode === "create") {
        await axiosClient.post(
          "/api/ppe/masters/product-variants",
          form
        );
      } else {
        await axiosClient.put(
          `/api/ppe/masters/product-variants/${record.id}`,
          form
        );
      }

      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        width: 440,
        height: "100vh",
        background: "#fff",
        boxShadow: "-4px 0 16px rgba(0,0,0,0.15)",
        zIndex: 2000,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: 16,
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h6 style={{ margin: 0 }}>
          {mode === "create"
            ? "Create Product Variant"
            : "Edit Product Variant"}
        </h6>
        <button className="btn btn-sm btn-light" onClick={onClose}>
          ✕
        </button>
      </div>

      {/* Body */}
      <div style={{ padding: 16, flex: 1, overflowY: "auto" }}>
        {error && <div className="alert alert-danger">{error}</div>}

        {/* Product Family */}
        <div className="mb-3">
          <label className="form-label">Product Family *</label>
          <select
            className="form-select"
            value={form.product_family_id}
            onChange={(e) =>
              onChange("product_family_id", e.target.value)
            }
          >
            <option value="">Select Product Family</option>
            {productFamilies.map((pf) => (
              <option key={pf.id} value={pf.id}>
                {pf.name}
              </option>
            ))}
          </select>
        </div>

        {/* Variant Code */}
        <div className="mb-3">
          <label className="form-label">Variant Code *</label>
          <input
            className="form-control"
            value={form.variant_code}
            onChange={(e) =>
              onChange("variant_code", e.target.value)
            }
          />
        </div>

        {/* Name */}
        <div className="mb-3">
          <label className="form-label">Variant Name *</label>
          <input
            className="form-control"
            value={form.name}
            onChange={(e) => onChange("name", e.target.value)}
          />
        </div>

        {/* Description */}
        <div className="mb-3">
          <label className="form-label">Description</label>
          <textarea
            className="form-control"
            rows={3}
            value={form.description}
            onChange={(e) =>
              onChange("description", e.target.value)
            }
          />
        </div>

        {/* Toggles */}
        <div className="form-check mb-2">
          <input
            className="form-check-input"
            type="checkbox"
            checked={form.drawing_required}
            onChange={(e) =>
              onChange("drawing_required", e.target.checked)
            }
          />
          <label className="form-check-label">
            Drawing Required
          </label>
        </div>

        <div className="form-check mb-2">
          <input
            className="form-check-input"
            type="checkbox"
            checked={form.routing_required}
            onChange={(e) =>
              onChange("routing_required", e.target.checked)
            }
          />
          <label className="form-check-label">
            Routing Required
          </label>
        </div>

        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            checked={form.is_active === 1}
            onChange={(e) =>
              onChange("is_active", e.target.checked ? 1 : 0)
            }
          />
          <label className="form-check-label">Active</label>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          padding: 16,
          borderTop: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
        }}
      >
        <button
          className="btn btn-outline-secondary"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          disabled={saving}
          onClick={onSubmit}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
